import { isHTMLElement } from '../../typeValidators';

/*
  Depth-first search for the deepest descendant node (text or element) matching `matcher`, preferring
  the first match in document order. Returns undefined if nothing matches. Descends into ALL child
  nodes, so it can return text nodes.

  `shouldDescendInto` prunes the walk: a node whose subtree can't contain a match (e.g. when only
  HTML elements can match, there's no point recursing into an <svg>) is skipped. It defaults to
  descending into everything, so text-node searches aren't pruned.
*/
export function getFirstNodeDescendantWith<NodeType extends Node = Node>(
  node: Node,
  matcher: (node: Node) => boolean,
  {
    direction = 'forward',
    shouldDescendInto = () => true,
  }: {
    direction?: 'forward' | 'reverse';
    shouldDescendInto?: (node: Node) => boolean;
  } = {},
): NodeType | undefined {
  if (!node.hasChildNodes()) {
    return;
  }

  let children = Array.from(node.childNodes);
  if (direction === 'reverse') {
    children = children.reverse();
  }

  for (const child of children) {
    const deeperMatch = shouldDescendInto(child)
      ? getFirstNodeDescendantWith<NodeType>(child, matcher, { direction, shouldDescendInto })
      : undefined;
    if (deeperMatch) {
      return deeperMatch;
    }
    if (matcher(child)) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- the caller asserts NodeType via the generic + matcher; it can't be proven structurally
      return child as unknown as NodeType;
    }
  }
}

/*
  Like getFirstNodeDescendantWith but searches from the end — the deepest LAST descendant.
*/
export function getLastNodeDescendantWith<NodeType extends Node = Node>(
  node: Node,
  matcher: (node: Node) => boolean,
  { shouldDescendInto }: { shouldDescendInto?: (node: Node) => boolean; } = {},
): NodeType | undefined {
  return getFirstNodeDescendantWith<NodeType>(node, matcher, { direction: 'reverse', shouldDescendInto });
}

/*
  The deepest FIRST descendant HTML element matching `matcher`. Only HTML elements can match, so
  non-HTML subtrees (e.g. <svg>) are skipped.
*/
export function getFirstHtmlElementDescendantWith<ElementType extends HTMLElement = HTMLElement>(
  element: HTMLElement,
  matcher: (element: HTMLElement) => boolean,
): ElementType | undefined {
  return getFirstNodeDescendantWith<ElementType>(
    element,
    (node) => isHTMLElement(node) && matcher(node),
    { shouldDescendInto: isHTMLElement },
  );
}

/*
  The deepest LAST descendant HTML element matching `matcher`. See getFirstHtmlElementDescendantWith.
*/
export function getLastHtmlElementDescendantWith<ElementType extends HTMLElement = HTMLElement>(
  element: HTMLElement,
  matcher: (element: HTMLElement) => boolean,
): ElementType | undefined {
  return getLastNodeDescendantWith<ElementType>(
    element,
    (node) => isHTMLElement(node) && matcher(node),
    { shouldDescendInto: isHTMLElement },
  );
}
